// @ts-check
// Revisão do envio (spec/whatsapp.md 18.3; contrato 09 de campanhas de WhatsApp, seção 14): abre com a pré-checagem do
// servidor e mostra quem recebe, quem fica de fora e por quê, o limite da Meta (hoje cabem, dias), a mensagem de exemplo,
// o custo de referência, avisos e bloqueios. No marketing, a confirmação do custo é obrigatória; com qualidade baixa,
// também a da qualidade. Termina enviando agora ou agendando (de 10 minutos a 90 dias).
import { html } from 'htm/preact';
import { createPortal } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { precheckWhatsappCampaign, scheduleWhatsappCampaign, startWhatsappCampaign } from '../../data/whatsapp-campaigns.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { Button } from '../../ui/button.js';
import { Checkbox, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatCurrency, formatDateTime, formatNumber } from '../../lib/format.js';
import {
  blockingNotices, campaignErrorReasonKey, canConfirmReview, readScheduleInput, requiredAcknowledgements, sampleValues, scheduleBounds,
} from '../../lib/whatsapp-campaigns.js';
import { WhatsappMessagePreview } from './whatsapp-message-preview.js';
import { deliveryReasonText } from './whatsapp-campaigns-page.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/whatsapp-campaigns.js').WhatsappCampaign} WhatsappCampaign
 * @typedef {import('../../data/whatsapp-campaigns.js').WhatsappCampaignPrecheck} WhatsappCampaignPrecheck
 */

/** Texto de um aviso ou bloqueio da pré-checagem, com os detalhes. @param {'warnings' | 'blockers'} group @param {{ code: string, [key: string]: any }} notice */
function noticeText(group, notice) {
  const values = {
    ...notice,
    days: String(notice.days ?? ''),
    // os textos com contagem são plurais: o t() escolhe a forma e formata o número
    count: Number(notice.count ?? 0),
    imported: formatNumber(Number(notice.imported ?? 0)),
    manual: formatNumber(Number(notice.manual ?? 0)),
    nextStart: notice.nextStart ? formatDateTime(notice.nextStart) : '',
    retryAt: notice.retryAt ? formatDateTime(notice.retryAt) : '',
    changeAt: notice.changeAt ? formatDateTime(notice.changeAt) : '',
  };
  const reasonKey = notice.reason ? `whatsappCampaigns.review.${group}.${notice.code}_${notice.reason}` : null;
  if (reasonKey && hasTranslation(reasonKey)) return t(reasonKey, values);
  const key = `whatsappCampaigns.review.${group}.${notice.code}`;
  return hasTranslation(key) ? t(key, values) : notice.code;
}

/** Erro da API em texto, com o motivo quando há. @param {unknown} error */
export function describeCampaignError(error) {
  if (error instanceof ApiError) {
    const reasonKey = campaignErrorReasonKey(error);
    if (reasonKey && hasTranslation(reasonKey)) return t(reasonKey);
    // códigos que as campanhas de e-mail também usam: o texto do WhatsApp fica na parte desta área
    const codeKey = `whatsappCampaigns.errorCodes.${error.code}`;
    if (hasTranslation(codeKey)) return t(codeKey);
  }
  return getErrorMessage(error);
}

/**
 * @param {{ campaign: WhatsappCampaign, template: any, onClose: () => void, onDone: (campaign: WhatsappCampaign) => void }} props
 */
export function WhatsappCampaignReviewModal({ campaign, template, onClose, onDone }) {
  const [precheck, setPrecheck] = useState(/** @type {WhatsappCampaignPrecheck | null} */ (null));
  const [loadError, setLoadError] = useState(/** @type {string | null} */ (null));
  const [acknowledgeMarketingCost, setAcknowledgeMarketingCost] = useState(false);
  const [acknowledgeLowQuality, setAcknowledgeLowQuality] = useState(false);
  const [mode, setMode] = useState(/** @type {'NOW' | 'SCHEDULE'} */ ('NOW'));
  const [scheduleValue, setScheduleValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(/** @type {string | null} */ (null));

  const load = () => {
    setLoadError(null);
    setPrecheck(null);
    precheckWhatsappCampaign(campaign.id).then(setPrecheck).catch((error) => setLoadError(describeCampaignError(error)));
  };
  useEffect(load, [campaign.id]);
  useEffect(() => {
    /** @param {KeyboardEvent} event */
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  const acknowledgements = { acknowledgeMarketingCost, acknowledgeLowQuality };
  const needed = requiredAcknowledgements(precheck);
  const blockers = blockingNotices(precheck, acknowledgements);
  const schedule = readScheduleInput(scheduleValue);
  const canSubmit = canConfirmReview(precheck, acknowledgements) && (mode === 'NOW' || schedule.problem === null) && !isSubmitting;
  const bounds = scheduleBounds();
  const sample = sampleValues(template ?? {}, precheck?.sample);

  const submit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const updated = mode === 'NOW'
        ? await startWhatsappCampaign(campaign.id, acknowledgements)
        : await scheduleWhatsappCampaign(campaign.id, { scheduledAt: /** @type {string} */ (schedule.scheduledAt), ...acknowledgements });
      showSuccessSnackbar(t(mode === 'NOW' ? 'whatsappCampaigns.review.started' : 'whatsappCampaigns.review.scheduled'));
      onDone(updated);
    } catch (error) {
      setSubmitError(describeCampaignError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loadError) {
      return html`<div class="wa-review-state" role="alert"><p>${loadError}</p>
        <${Button} title=${t('whatsappCampaigns.list.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${load} /></div>`;
    }
    if (!precheck) return html`<div class="wa-review-state" aria-busy="true"><${Skeleton} height=${18} /><${Skeleton} height=${18} width="60%" /><${Skeleton} height=${120} /></div>`;
    const excluded = Object.entries(precheck.audience.excluded ?? {}).filter(([, count]) => Number(count) > 0).sort((left, right) => Number(right[1]) - Number(left[1]));
    return html`
      <section class="wa-review-section" aria-labelledby="wa-review-audience">
        <h3 id="wa-review-audience">${t('whatsappCampaigns.review.audience')}</h3>
        <p class="wa-review-highlight" data-testid="wa-review-to-send">${t('whatsappCampaigns.review.toSend', { count: precheck.toSend, cards: formatNumber(precheck.audience.cards) })}</p>
        ${precheck.audience.duplicates > 0 ? html`<p>${t('whatsappCampaigns.review.duplicates', { count: precheck.audience.duplicates })}</p>` : null}
        ${precheck.audience.serviceWindowOpen > 0 ? html`<p>${t('whatsappCampaigns.review.serviceWindow', { count: precheck.audience.serviceWindowOpen })}</p>` : null}
        ${excluded.length > 0
          ? html`<ul class="wa-review-exclusions">${excluded.map(([reason, count]) => html`<li key=${reason}><span>${deliveryReasonText(reason)}</span><strong>${formatNumber(Number(count))}</strong></li>`)}</ul>`
          : null}
      </section>
      <section class="wa-review-section" aria-labelledby="wa-review-limit">
        <h3 id="wa-review-limit">${t('whatsappCampaigns.review.limit')}</h3>
        ${precheck.portfolio
          ? html`<p>${precheck.portfolio.limit === null
              ? t('whatsappCampaigns.limits.unlimited', { used: formatNumber(precheck.portfolio.usedLast24h) })
              : t('whatsappCampaigns.limits.banner', { limit: formatNumber(precheck.portfolio.limit), used: formatNumber(precheck.portfolio.usedLast24h), cap: formatNumber(precheck.portfolio.cap ?? 0) })}</p>
            ${precheck.portfolio.availableNow !== null ? html`<p>${t('whatsappCampaigns.limits.todayFits', { count: precheck.portfolio.availableNow })}</p>` : null}
            ${precheck.portfolio.estimatedDays && precheck.portfolio.estimatedDays > 1 ? html`<p>${t('whatsappCampaigns.limits.days', { days: precheck.portfolio.estimatedDays })}</p>` : null}`
          : html`<p>${t('whatsappCampaigns.review.limitUnknown')}</p>`}
        <p>${precheck.sendWindow.enabled
          ? (precheck.sendWindow.isOpenNow ? t('whatsappCampaigns.review.sendWindowOpen') : t('whatsappCampaigns.review.sendWindowClosed', { when: formatDateTime(precheck.sendWindow.nextStart) }))
          : t('whatsappCampaigns.review.sendWindowOff')}</p>
      </section>
      <section class="wa-review-section" aria-labelledby="wa-review-message">
        <h3 id="wa-review-message">${t('whatsappCampaigns.review.message')}</h3>
        ${precheck.sample?.to ? html`<p class="wa-review-sample-to">${t('whatsappCampaigns.review.sampleTo', { name: precheck.sample.to.displayName })}</p>` : null}
        <${WhatsappMessagePreview} template=${template} values=${sample.values} title=${null}
          samples=${precheck.sample ? [{ values: sample.values, fallbackVariables: sample.fallbackVariables }] : null} />
        ${sample.fallbackVariables.length > 0 ? html`<p class="wa-review-note">${t('whatsappCampaigns.review.fallbackUsed', { variables: sample.fallbackVariables.map((name) => `{{${name}}}`).join(', ') })}</p>` : null}
      </section>
      <section class="wa-review-section" aria-labelledby="wa-review-cost">
        <h3 id="wa-review-cost">${t('whatsappCampaigns.review.cost')}</h3>
        <p>${precheck.cost?.isAvailable
          ? t('whatsappCampaigns.review.costValue', {
            total: formatCurrency(Number(precheck.cost.estimatedTotal ?? 0), precheck.cost.currency ?? 'BRL'),
            unit: formatCurrency(Number(precheck.cost.unitPrice ?? 0), precheck.cost.currency ?? 'BRL', { decimals: 4 }),
          })
          : t('whatsappCampaigns.review.costUnavailable')}</p>
      </section>
      ${precheck.warnings.length > 0
        ? html`<section class="wa-review-section" aria-labelledby="wa-review-warnings">
            <h3 id="wa-review-warnings">${t('whatsappCampaigns.review.warningsTitle')}</h3>
            <ul class="wa-review-notices">${precheck.warnings.map((notice, index) => html`<li key=${`${notice.code}-${index}`} data-tone="warning" data-code=${notice.code}>
              <${Icon} name="IconAlertTriangle" size=${14} /><span>${noticeText('warnings', notice)}</span></li>`)}</ul>
          </section>`
        : null}
      ${blockers.length > 0
        ? html`<section class="wa-review-section" aria-labelledby="wa-review-blockers">
            <h3 id="wa-review-blockers">${t('whatsappCampaigns.review.blockersTitle')}</h3>
            <ul class="wa-review-notices">${blockers.map((notice, index) => html`<li key=${`${notice.code}-${index}`} data-tone="danger" data-code=${notice.code}>
              <${Icon} name="IconBan" size=${14} /><span>${noticeText('blockers', notice)}</span></li>`)}</ul>
          </section>`
        : null}
      ${needed.length > 0
        ? html`<section class="wa-review-section wa-review-acks">
            ${needed.includes('MARKETING_COST')
              ? html`<div class="wa-review-ack"><${Checkbox} checked=${acknowledgeMarketingCost} onChange=${setAcknowledgeMarketingCost} ariaLabel=${t('whatsappCampaigns.review.acknowledgeCost')} />
                  <span onClick=${() => setAcknowledgeMarketingCost(!acknowledgeMarketingCost)}>${t('whatsappCampaigns.review.acknowledgeCost')}</span></div>`
              : null}
            ${needed.includes('LOW_QUALITY')
              ? html`<div class="wa-review-ack"><${Checkbox} checked=${acknowledgeLowQuality} onChange=${setAcknowledgeLowQuality} ariaLabel=${t('whatsappCampaigns.review.acknowledgeQuality')} />
                  <span onClick=${() => setAcknowledgeLowQuality(!acknowledgeLowQuality)}>${t('whatsappCampaigns.review.acknowledgeQuality')}</span></div>`
              : null}
          </section>`
        : null}
      <section class="wa-review-section wa-review-when">
        <label class="wa-review-mode"><input type="radio" name="wa-review-mode" checked=${mode === 'NOW'} onChange=${() => setMode('NOW')} />${t('whatsappCampaigns.editor.sendNow')}</label>
        <label class="wa-review-mode"><input type="radio" name="wa-review-mode" checked=${mode === 'SCHEDULE'} onChange=${() => setMode('SCHEDULE')} />${t('whatsappCampaigns.editor.schedule')}</label>
        ${mode === 'SCHEDULE'
          ? html`<label class="form-field">
              <span class="form-field-label">${t('whatsappCampaigns.review.scheduleAt')}</span>
              <input type="datetime-local" class="text-input" min=${bounds.min} max=${bounds.max} value=${scheduleValue}
                onInput=${(/** @type {Event} */ event) => setScheduleValue(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
              ${scheduleValue && schedule.problem ? html`<span class="wa-field-error" role="alert">${t(`whatsappCampaigns.review.scheduleProblems.${schedule.problem}`)}</span>` : null}
            </label>`
          : null}
      </section>
      ${submitError ? html`<p class="wa-field-error" role="alert">${submitError}</p>` : null}`;
  };

  return createPortal(html`<div class="modal-backdrop" onMouseDown=${(/** @type {MouseEvent} */ event) => event.target === event.currentTarget && onClose()}>
    <div class="modal wa-review-modal" role="dialog" aria-modal="true" aria-label=${t('whatsappCampaigns.review.title')}>
      <h2 class="modal-title">${t('whatsappCampaigns.review.title')}</h2>
      <p class="modal-subtitle">${campaign.name}</p>
      <div class="wa-review-body">${renderBody()}</div>
      <div class="modal-actions">
        <${Button} title=${t('whatsappCampaigns.review.back')} variant="secondary" size="small" onClick=${onClose} />
        <${Button} title=${mode === 'NOW' ? t('whatsappCampaigns.editor.sendNow') : t('whatsappCampaigns.editor.schedule')} accent="blue" size="small"
          disabled=${!canSubmit} isLoading=${isSubmitting} onClick=${submit} data-testid="wa-review-confirm" />
      </div>
    </div>
  </div>`, document.body);
}
